import React, { useContext, useEffect, useMemo, useState } from 'react'
import { getPrice, translate } from '../utils/translator'
import { useParams } from 'react-router-dom'
import { useCartContext } from '../customHooks/useCart'
import { ShippingDataContext } from '../pages/Shipping/Shipping'
import { useShippingPageContext } from '../pages/v2/ShippingV2/providers/ShippingPageProvider'
import { ImSpinner3 } from "react-icons/im";
import { MainTracker } from "../PixelTrackers/MainTracker";
import DisclaimerTooltip from './DisclaimerTooltip';
import { TOOLTIP_SHIPPING_FEE, OTC_PRODUCT_TYPES, DRUGS_PRODUCT_TYPES } from '../utils/consts';
import { isZeroOrderPrice } from '../utils/helpers';

type Props = {
    setRxProductFinalPrice?: (price: number) => void
    shippingFee?: number
    isExpressShipping?: boolean
    trackFinalCartPrice?: boolean
    isOtcOnly?: boolean
    showDelayedCaptureDisclaimer?: boolean
}

const CANNABIS_ID_TITLE = "Digitaler Cannabis-Ausweis";
const FREE_TOTAL_BADGE_BACKGROUND = "#11DDAC";
const FREE_TOTAL_PRICE_COLOR = "#0A9281";
const CartViewSummary: React.FC<Props> = ({ setRxProductFinalPrice, shippingFee, trackFinalCartPrice=false, isExpressShipping=false, isOtcOnly=false, showDelayedCaptureDisclaimer=false }) => {

    const [openTooltip, setOpenTooltip] = useState<string | null>(null);
    const { language } = useParams()
    const { getCart, cartCurrency } = useCartContext();
    const cart = getCart();
    const { cannabisIDState } = useContext(ShippingDataContext) || {}; // Shipping page
    const { expressDeliveryState } = useShippingPageContext();
    const expressDeliveryPrice = expressDeliveryState?.deliveryPrice || 0;
    const deliveryFeeIsLoading = expressDeliveryState?.deliveryPriceLoading;
    const cannbisIdAsOrderProduct = cart.products.find(product => (product.type === "cannabis_id" || product.title === CANNABIS_ID_TITLE));
    const videoConsultationAsOrderProduct = cart.products.find(product => product.type === "online_vd"); // There is no other way to get this without breaking some types
    const hasPharmacyOnlyMedicine = cart.products.some(product => product.type === "pharmacy_only_medicine");
    
    // An Rx-only cart still ships its OTC items, and the backend charges the OTC threshold fee for
    // it (OrderManagerV2 — not country-gated). Presence-based, matching the backend's own
    // cartContainsNonTestLabOtc check rather than the price-based total_otc_drugs row.
    const hasOtcOnRxOnlyCart = Boolean(cart.only_prescription)
        && cart.products.some(product => product.type && OTC_PRODUCT_TYPES.includes(product.type));

    // calculate custom shipping fee value
    const actualShippingFee = useMemo(() => {
        if (isExpressShipping && expressDeliveryPrice > 0) {
            return expressDeliveryPrice;
        }
        if (cart.only_prescription) {
            // Mixed Rx+OTC: show the fee getCartView recomputed (never the stored custom_shipping_fee,
            // which can hold a stale express quote). Pure Rx-only keeps 0 so no fee reaches the total.
            return hasOtcOnRxOnlyCart ? cart.shipping_fee || 0 : 0;
        }
        return shippingFee || 0;
    }, [isExpressShipping, expressDeliveryPrice, shippingFee, cart.only_prescription, cart.shipping_fee, hasOtcOnRxOnlyCart]);

    
    const summaryRows = useMemo(() => {
        const totalDrugsPrice = cart.only_prescription ? 0 : cart.products.filter(product => product.type && DRUGS_PRODUCT_TYPES.includes(product.type)).reduce((sum, cartProduct) => { 
            const basePrice = cartProduct.official_price + (cartProduct.service_fee || 0);
            return sum += basePrice * cartProduct.quantity
        }, 0);

        const totalOTCPrice = cart.products.filter(product => product.type && OTC_PRODUCT_TYPES.includes(product.type)).reduce((sum, cartProduct) => sum += cartProduct.item_price * cartProduct.quantity, 0);
        return {
            treatment_fee: {
                shouldShow: hasPharmacyOnlyMedicine || (!isOtcOnly && (cart.treatment_fee !== 0)),
                value: cart.treatment_fee || 0, //Treatment fee defined as the maximum value of the treatment fee of all products in the cart
            },

            consultation_fee: {
                shouldShow: cart.consultation_fee === 0 ? false : true,
                value: cart.consultation_fee || 0,
            },

            total_drugs: {
                shouldShow: Boolean(!cart.only_prescription) && totalDrugsPrice > 0,
                value: cart.only_prescription ? 0 : totalDrugsPrice
            },

            total_otc_drugs: {
                shouldShow: totalOTCPrice > 0,
                value: totalOTCPrice
            },

            cannabis_id: {
                shouldShow: Boolean(cannbisIdAsOrderProduct) || Boolean(cannabisIDState?.isCannabisIDAddedToOrder),
                value: cart.only_prescription ? (cannbisIdAsOrderProduct?.item_price ? cannbisIdAsOrderProduct.item_price : 0) : 0
            },

            shipping_fee: {
                shouldShow: videoConsultationAsOrderProduct ? false : true,
                value: 0,
            },

            coupon: {
                shouldShow: Boolean(cart.bonus_value) && !Boolean(cart.applied_subscription_discount) && Boolean(cart.only_prescription ? !cart.auto_bonus : true), // Auto bonus should not be applied on only prescription
                value: (-1) * Number(cart.bonus_value),
            },

            subscription_discount: {
                shouldShow: Boolean(cart.applied_subscription_discount),
                value: (-1) * Number(cart.applied_subscription_discount),
            }
        }

    },[cart, cart.locale_id, cart.products, cannabisIDState?.isCannabisIDAddedToOrder, cannabisIDState?.cost]);


    const shippingFeeRowElement = useMemo(() => {
        let text;

        if (cart.self_pickup_pharmacy_id ) {
            text = translate(language, "default", "OrderWindow/self_pickup");
        }

        // handle shipping text
        else if (cart.only_prescription && !(hasOtcOnRxOnlyCart && actualShippingFee > 0)) { //TODO: check if it's subsidized (mainly for FR catalogs) and if so, we need to show the shipping fee
            // text = translate(language, "default", "OrderWindow/prescription_delivery");
            // value = DISCOUNTED_SHIPPING_FEE
            return <></>

        } else if (isExpressShipping) {
            text = translate(language, "default", "OrderWindow/express_delivery");
        } else {
            // Test-lab kits ship in 2-4 business days (the medication text says 1-2).
            const hasTestLabKit = cart.products?.some((product) => product.is_test_lab && Number(product.quantity) > 0);
            text = translate(language, "default", hasTestLabKit ? "OrderWindow/test_lab_delivery" : "OrderWindow/next_day_delivery");
        }

        if (deliveryFeeIsLoading && isExpressShipping) {
            return (
                <div className='flex justify-center items-center h-full'>
                    <ImSpinner3 className="animate-spin text-2xl" />
                </div>
            )
        }

        return (
            <li id="cart-view-summary-row" className="flex justify-between items-center text-sm text-[#656D79]">
                <div className="flex items-center gap-2">
                    <p id="cart-view-summary-row-text" className="font-roboto"> {text} </p>
                    {!!isExpressShipping && (
                        <DisclaimerTooltip
                            isOpen={openTooltip === TOOLTIP_SHIPPING_FEE}
                            onToggle={() => setOpenTooltip(openTooltip === TOOLTIP_SHIPPING_FEE ? null : TOOLTIP_SHIPPING_FEE)}
                            tooltipText={(() => {
                            const doctorApprovalFee = cart.fast_approval_fee ?? 0;
                            const customShipping = cart.custom_shipping_fee ?? 0;
                            const deliveryFee = expressDeliveryPrice > 0
                                ? Math.max(0, expressDeliveryPrice - doctorApprovalFee)
                                : (doctorApprovalFee > 0 && customShipping >= 10
                                    ? Math.max(0, customShipping - doctorApprovalFee)
                                    : customShipping);
                            return translate(language, "default", "OrderWindow/express_delivery_price_tooltip")
                            .replace(':deliveryFee', getPrice(deliveryFee, cartCurrency.locale, cartCurrency.rate))
                            .replace(':doctorApprovalFee', getPrice(doctorApprovalFee, cartCurrency.locale, cartCurrency.rate));
                        })()}
                            tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
                        />
                    )}
                </div>

                <span id='cart-view-summary-row-value' className={`font-roboto ${actualShippingFee === 0 ? 'font-bold text-[#269C6E]' : actualShippingFee !== 0 && !isExpressShipping && !hasOtcOnRxOnlyCart ? "line-through" : ""}`} translate="no">
                    {actualShippingFee === 0
                        ? translate(language, "default", "OrderWindow/shipping_fee_free")
                        : getPrice(actualShippingFee, cartCurrency.locale, cartCurrency.rate)}
                </span>
            </li>
        )

    }, [cart, cartCurrency, isExpressShipping, actualShippingFee, deliveryFeeIsLoading, language]) // add openTooltip to here

    useEffect(() => {
    //TODO: This is backwards compatibility for old shipping, remove this once Shipping.js is deprecated
        if (setRxProductFinalPrice){ 
            setRxProductFinalPrice(summaryRows.total_drugs.value + summaryRows.treatment_fee.value + summaryRows.subscription_discount.value + summaryRows.coupon.value);
        }
    }, [summaryRows])






    const cartTotalPrice = useMemo(() => {

        //Sum the total of summaryRows
        let sum = 0;
        
        Object.entries(summaryRows).forEach(([field, summaryRow]) => {
            if (summaryRow.shouldShow) {
                sum += summaryRow.value;
            }
        })

        sum += actualShippingFee;

        return sum

    }, [cart, language, summaryRows, actualShippingFee]);
    const isCartTotalFree = isZeroOrderPrice(cartTotalPrice);
    const originalTreatmentFee = summaryRows.treatment_fee.value;
    const shouldShowStruckOriginalTreatmentFee = isCartTotalFree && originalTreatmentFee > 0;
    const displayedTreatmentFee = isCartTotalFree ? 0 : originalTreatmentFee;
    useEffect(() => {
        const cartFinalPriceElement = document.getElementById("cart_final_price");
        if (cartFinalPriceElement){
            if (sessionStorage.getItem('paymentEventSent') && trackFinalCartPrice){          
                MainTracker.track("products", "Final Cart Price", {"Cart Price": cartFinalPriceElement.textContent, "Customer ID": sessionStorage.getItem('paymentEventSent')});
                sessionStorage.removeItem('paymentEventSent');

            }
        }
    }, [cartTotalPrice]);


    return (
        <>
        <ul id='cart-view-summary' className='flex flex-col gap-y-1'>

            {/* Treatment Fee  */}
            {
                summaryRows["treatment_fee"].shouldShow &&
                <li id="cart-view-summary-row-treatment-fee" className="flex justify-between items-center text-sm text-[#656D79]">
                    <p id="cart-view-summary-row-text-treatment-fee" className="font-roboto">
                        {translate(language, "default", "OrderWindow/treatment_fee")}
                    </p>
                    
                   <div className="flex items-center gap-2">
                       {shouldShowStruckOriginalTreatmentFee && (
                           <span className="font-roboto line-through" translate="no">
                               {getPrice(originalTreatmentFee, cartCurrency.locale, cartCurrency.rate)}
                           </span>
                       )}
                       <span
                           id='cart-view-summary-row-value-treatment-fee'
                           className="font-roboto"
                           style={isCartTotalFree ? { color: FREE_TOTAL_PRICE_COLOR } : undefined}
                           translate="no"
                       >
                            {getPrice(displayedTreatmentFee, cartCurrency.locale, cartCurrency.rate)}
                        </span>
                   </div>
                </li>
            }

            {/* Drugs Price  */}
            {
                summaryRows["total_drugs"].shouldShow &&
                <li id="cart-view-summary-row-total-drugs" className="flex justify-between items-center text-sm text-[#656D79]">
                    <p id="cart-view-summary-row-text-total-drugs" className="font-roboto">
                        {translate(language, "default", "OrderWindow/drug_price")}
                    </p>
                    <span id='cart-view-summary-row-value-total-drugs' className="font-roboto" translate="no">
                        {getPrice(summaryRows["total_drugs"].value, cartCurrency.locale, cartCurrency.rate)}
                    </span>
                </li>
            }


            {/* OTC Drugs Price  */}
            {
                summaryRows["total_otc_drugs"].shouldShow &&
                <li id="cart-view-summary-row-total-otc-drugs" className="flex justify-between items-center text-sm text-[#656D79]">
                    <p id="cart-view-summary-row-text-total-otc-drugs" className="font-roboto">
                        {translate(language, "default", "OrderSummary/otc_addons_price")}
                    </p>
                    <span id='cart-view-summary-row-value-total-otc-drugs' className="font-roboto" translate="no">
                        {getPrice(summaryRows["total_otc_drugs"].value, cartCurrency.locale, cartCurrency.rate)}
                    </span>
                </li>
            }



            {/* Consultation Fee  */}
            {summaryRows['consultation_fee'].shouldShow && (
                <li id="cart-view-summary-row-consultation-fee" className="flex justify-between items-center text-sm text-[#656D79]">
                    <p className="font-roboto">{translate(language, 'default', cart.is_follow_up_consultation ? 'OrderWindow/consultation_fee_follow_up' : 'OrderWindow/consultation_fee')}:</p>
                    <span id="cart-view-summary-row-value-consultation_fee" className="font-roboto" translate="no">
                        {' '}
                        {getPrice(summaryRows['consultation_fee'].value, cartCurrency.locale, cartCurrency.rate)}{' '}
                    </span>
                </li>
            )}


            {/* Shipping Fee  */}
            {summaryRows["shipping_fee"].shouldShow && shippingFeeRowElement}


            {/* Digital Cannabis ID Price row  */}
            {summaryRows["cannabis_id"].shouldShow &&
                <li id='cart-view-summary-row-cannabis-id' className='flex justify-between items-center text-sm text-[#656D79]'>
                    <p className='font-roboto'>Digitaler Cannabis-Ausweis</p>
                    <span id='cart-view-summary-row-value-cannabis_id' className="font-roboto" translate="no"> {getPrice(summaryRows["cannabis_id"].value, cartCurrency.locale, cartCurrency.rate)} </span>
                </li>
            }


            {/* Coupon discount */}
            {
                summaryRows["coupon"].shouldShow &&
                <li id='cart-view-summary-row-coupon' className='flex justify-between items-center text-sm text-[#656D79]'>
                    <p className='font-roboto'> {translate(language, "default", "OrderWindow/coupon")} </p>
                    <span className='font-roboto text-[#269C6E]' translate="no"> {getPrice(summaryRows["coupon"].value, cartCurrency.locale, cartCurrency.rate)} </span>
                </li>
            }


            {/* Applied subscription discount */}
            {
                summaryRows["subscription_discount"].shouldShow &&
                <li id='cart-view-summary-row-subscription-discount' className='flex justify-between items-center text-sm text-[#656D79]'>
                    <p className='font-roboto'> {translate(language, "default", "OrderWindow/treatment_plan_discount")} </p>
                    <span className='font-roboto text-[#269C6E]' translate="no"> {getPrice(summaryRows["subscription_discount"].value, cartCurrency.locale, cartCurrency.rate)} </span>
                </li>
            }


            {/* Cart total Price */}
            <li id='cart-view-summary-row-total-price' className='flex justify-between items-center text-sm text-[#656D79]'>
                <p className='font-roboto font-semibold text-[#0D2C54] text-lg'> {translate(language, "default", "OrderWindow/total_price")} </p>
                <div className={isCartTotalFree ? 'flex items-center gap-2' : 'flex flex-col items-end'}>
                    {isCartTotalFree && (
                        <span
                            className="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                            style={{ backgroundColor: FREE_TOTAL_BADGE_BACKGROUND }}
                        >
                            {translate(language, "default", "OrderWindow/free_badge") || "FREE"}
                        </span>
                    )}
                    <span
                        id="cart_final_price"
                        className={isCartTotalFree ? 'font-roboto font-semibold' : 'font-roboto font-semibold text-[#0D2C54]'}
                        style={isCartTotalFree ? { color: FREE_TOTAL_PRICE_COLOR } : undefined}
                        translate="no"
                    >
                        {getPrice(cartTotalPrice, cartCurrency.locale, cartCurrency.rate)}
                    </span>
                </div>
            </li>
        </ul>
        </>
    )
}

export default CartViewSummary;